import * as Papa from 'papaparse';
import type { Curve, ParsedData, ParsedToolData } from '../../../interfaces/toolData';

/**
 * Parser for Innova battery export CSV files
 * Implements the ParsedToolData interface for consistent data handling
 */
export class InnovaBatteryParser {
  /**
   * Parse Innova battery export CSV file content
   * @param fileContent - Raw CSV file content as string
   * @param filename - Original filename for metadata
   * @returns Parsed data conforming to ParsedToolData interface
   */
  parse(fileContent: string, _filename: string = ''): ParsedData {
    try {
      // Check if content is valid
      if (!fileContent.trim()) {
        throw new Error('File is empty');
      }
      
      // Check if it looks like an Innova battery file
      if (!isInnovaBatteryFile(fileContent)) {
        throw new Error('File does not appear to be an Innova battery export');
      }
      
      const lines = fileContent.split('\n');
      
      // Find header and data sections
      const headerEndIndex = this.findHeaderEnd(lines);
      const metadata = this.parseMetadata(lines.slice(0, headerEndIndex));
      
      // Parse CSV data section
      const csvData = lines.slice(headerEndIndex).join('\n');
      const parseResult = Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
      });

      if (parseResult.errors.length > 0) {
        console.warn('CSV parsing warnings:', parseResult.errors);
      }

      const rawData = parseResult.data as Record<string, string>[];
      
      // Convert to ParsedToolData format
      const curves = this.convertToCurves(rawData);
      
      const timeSeriesData: ParsedToolData = {
        toolType: 'Innova',
        fileType: 'INNOVA_BATTERY_DEFAULT',
        curves,
        metadata,
        dataPreview: {
          first5: rawData.slice(0, 5),
          last5: rawData.slice(-5),
          totalCount: rawData.length
        },
        errors: parseResult.errors?.map(e => e.message)
      };

      return timeSeriesData;
      
    } catch (error) {
      throw new Error(`Failed to parse Innova battery file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Find where the header section ends and data begins
   */
  private findHeaderEnd(lines: string[]): number {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Look for CSV header row - either multi-column with commas or single column
      const hasRelevantTerms = line.includes('Time') || line.includes('Timestamp') || 
                               line.includes('Current') || line.includes('Voltage') || 
                               line.includes('Battery');
      
      if (hasRelevantTerms) {
        // Check if this is a units row (contains units like 'mA', 'V', '°C')
        if (line.includes('mA') || line.includes('°C') || line.includes('g RMS')) {
          // Skip units row, data starts on next line
          return i + 1;
        }
        
        // This is a column header row
        if (line.includes(',')) {
          // Multi-column CSV, check if next line is units
          if (i + 1 < lines.length && 
              (lines[i + 1].includes('mA') || lines[i + 1].includes('°C') || lines[i + 1].includes('g RMS'))) {
            return i + 2; // Skip header and units row
          }
          return i + 1; // Just skip header row
        } else {
          // Single column header, data starts next line
          return i + 1;
        }
      }
    }
    // Default to line 0 if no clear header found
    return 0;
  }

  /**
   * Parse metadata from header lines
   */
  private parseMetadata(headerLines: string[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    for (const line of headerLines) {
      if (line.includes(':')) {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim();
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return metadata;
  }

  /**
   * Convert raw CSV data to curves format
   */
  private convertToCurves(rawData: Record<string, string>[]): Curve[] {
    if (rawData.length === 0) {
      return [];
    }

    const curves: Curve[] = [];
    
    // Check if we have valid data rows with content
    const firstRow = rawData[0];
    if (!firstRow || Object.keys(firstRow).length === 0) {
      return [];
    }
    
    const timeColumnName = this.findTimeColumn(firstRow);
    
    if (!timeColumnName) {
      throw new Error('No time column found in Innova battery data');
    }

    // Create curves for each data column (excluding ALL time-related columns)
    const dataColumns = Object.keys(rawData[0]).filter(col => !this.isTimeRelatedColumn(col));
    

    
    for (const columnName of dataColumns) {
      // Extract values, handling NaN, empty strings, and missing values
      const yValues: number[] = [];
      const xValues: Date[] = [];
      
      let validRows = 0;
      let invalidTimeRows = 0;
      let invalidValueRows = 0;
      
      for (const row of rawData) {
        const rawValue = row[columnName];
        const timeValue = row[timeColumnName];
        
        // Skip rows with invalid time
        if (!timeValue || timeValue.trim() === '') {
          invalidTimeRows++;
          continue;
        }
        
        // Include data points that have valid values, filter out NaN/empty
        if (rawValue && rawValue.trim() !== '' && rawValue.toLowerCase() !== 'nan') {
          const numericValue = parseFloat(rawValue);
          if (!isNaN(numericValue)) {
            yValues.push(numericValue);
            // Parse MM/dd/yyyy HH:mm:ss.SSS format explicitly
            const dateObj = this.parseInnovaTimestamp(timeValue);
            xValues.push(dateObj);
            validRows++;
          } else {
            invalidValueRows++;
          }
        } else {
          invalidValueRows++;
        }
      }

      
      // Only create curve if we have valid data points
      if (yValues.length === 0) continue;
      
      const curve: Curve = {
        name: columnName,
        xValues, // Use filtered timestamps that correspond to valid data
        yValues,
        yUnit: this.getUnitForColumn(columnName)
      };
      
      curves.push(curve);
    }

    return curves;
  }

  /**
   * Find the primary time column to use for X-axis timestamps
   */
  private findTimeColumn(firstRow: Record<string, string>): string | null {
    const timeColumnNames = ['Time', 'Timestamp', 'DateTime', 'time', 'timestamp'];
    
    for (const columnName of timeColumnNames) {
      if (columnName in firstRow) {
        return columnName;
      }
    }
    
    return null;
  }

  /**
   * Parse Innova timestamp format: MM/dd/yyyy HH:mm:ss.SSS
   */
  private parseInnovaTimestamp(timeValue: string): Date {
    // Format: "07/24/2025 03:05:35.000"
    // Convert to ISO format for reliable parsing
    const parts = timeValue.trim().split(' ');
    if (parts.length !== 2) {
      throw new Error(`Invalid timestamp format: ${timeValue}`);
    }
    
    const [datePart, timePart] = parts;
    const dateComponents = datePart.split('/');
    if (dateComponents.length !== 3) {
      throw new Error(`Invalid date format: ${datePart} in ${timeValue}`);
    }
    
    const [month, day, year] = dateComponents;
    if (!month || !day || !year) {
      throw new Error(`Missing date components in: ${timeValue}`);
    }
    
    // Convert to ISO format: YYYY-MM-DDTHH:mm:ss.SSSZ
    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}Z`;
    
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      throw new Error(`Failed to parse timestamp: ${timeValue} → ${isoString}`);
    }
    
    return date;
  }

  /**
   * Check if a column is time-related and should be excluded from data curves
   */
  private isTimeRelatedColumn(columnName: string): boolean {
    const name = columnName.toLowerCase();
    
    // Exact matches for known Innova time column names
    const timeColumnNames = [
      'time', 'timestamp', 'datetime', 'date', 'time of day', 'time(sec)'
    ];
    
    if (timeColumnNames.includes(name)) {
      return true;
    }
    
    // Pattern matches for time-related columns
    return name.includes('time') || 
           name.includes('date') || 
           name.includes('timestamp') ||
           !!name.match(/^time\s*\(/i); // Time(sec), Time(ms), etc.
  }

  /**
   * Get appropriate unit for a given column name
   */
  private getUnitForColumn(columnName: string): string {
    const name = columnName.toLowerCase();
    
    // Check specific patterns first before general ones
    if (name.includes('capacity')) return 'Ah';
    if (name.includes('voltage') || name.includes('_v')) return 'V';
    if (name.includes('current') || name.includes('_a')) return 'A';
    if (name.includes('temperature') || name.includes('temp')) return '°C';
    if (name.includes('power')) return 'W';
    if (name.includes('resistance')) return 'Ω';
    if (name.includes('percent') || name.includes('%')) return '%';
    
    return 'units';
  }
}

/**
 * Check if file content appears to be an Innova battery export
 */
export function isInnovaBatteryFile(fileContent: string): boolean {
  return fileContent.includes('Battery:') || 
         /Innova\s+Battery\s+Export/i.test(fileContent) ||
         fileContent.includes('Battery Export') ||
         fileContent.includes('INNOVA_BATTERY');
} 